import { Typography, TypographyProps } from "@mui/material";
import { useTranslation } from "react-i18next";

/**
 * Converts HSL hue and saturation with a predefined lightness category
 * into a full HSL CSS color string.
 *
 * @param {number} hue - The hue (0–360).
 * @param {number} saturation - The saturation (0–100).
 * @param {"dark" | "medium" | "light"} lightness - Preset lightness level.
 * @returns {string} A CSS-compatible HSL color string.
 */
const getHSLColor = (
  hue: number,
  saturation: number,
  lightness: "dark" | "medium" | "light"
): string => {
  const lightnessValues = {
    dark: 10,
    medium: 50,
    light: 70,
  };

  return `hsl(${hue}, ${saturation}%, ${lightnessValues[lightness]}%)`;
};

/**
 * Mapping of abstract font weight keywords to numeric values.
 * @readonly
 * @type {{ thin: 300, medium: 500, bold: 700 }}
 */
const fontWeightMapping: { thin: 300; medium: 500; bold: 700; } = {
  thin: 300,
  medium: 500,
  bold: 700,
} as const;

type FontWeight = keyof typeof fontWeightMapping;
type Lightness = "dark" | "medium" | "light";

/**
 * Extended props for styled, translatable typography components.
 * @typedef {Object} TypographyPropsWithStyle
 * @property {string} children - The translatable text content (i18n key).
 * @property {object} [sx] - Optional style overrides (MUI sx prop).
 * @property {string | number} [fontSize] - Font size override.
 * @property {FontWeight} [fontWeight] - Font weight ("thin", "medium", "bold").
 * @property {Lightness} [hslLightness] - Lightness level for HSL color.
 * @property {{ hue: number, saturation: number }} [hsl] - HSL color config.
 */
interface TypographyPropsWithStyle {
  children: React.ReactNode;
  sx?: object;
  fontSize?: string | number;
  fontWeight?: FontWeight;
  hslLightness?: Lightness;
  hsl?: {
    hue: number;
    saturation: number;
  };
}

const defaultHSL = { hue: 0, saturation: 0, lightness: "medium" };

/**
 * Internal component used to render text with consistent typography styling.
 *
 * @param {TypographyPropsWithStyle & { variant: TypographyProps["variant"], component: React.ElementType }} props
 * @returns {JSX.Element}
 */
const CustomTypography = ({
  children,
  sx,
  fontSize,
  fontWeight,
  hslLightness = "medium",
  hsl = defaultHSL,
  variant,
  component,
}: TypographyPropsWithStyle & {
  variant: TypographyProps["variant"];
  component: React.ElementType;
}): JSX.Element => {
  const { t, i18n } = useTranslation();
  const hslColor = getHSLColor(hsl.hue, hsl.saturation, hslLightness);

  const isString = typeof children === "string";
  const isTranslationMissing = isString && !i18n.exists(children);

  if (isTranslationMissing) {
    console.error(new Error(`[i18n] Missing translation: "${children}"`));
  }

  return (
    <Typography
      variant={variant}
      component={component}
      sx={{
        ...sx,
        fontSize,
        fontWeight: fontWeight ? fontWeightMapping[fontWeight] : undefined,
        color: hslColor,
      }}
    >
      {isString ? t(children as string) : children}
    </Typography>
  );
};


/**
 * Renders an H1 heading for primary page titles.
 *
 * @param {TypographyPropsWithStyle} props
 * @returns {JSX.Element}
 */
export const Heading = ({
  children,
  sx,
  fontSize = "2rem",
  fontWeight = "bold",
  hslLightness = "dark",
  hsl = defaultHSL,
}: TypographyPropsWithStyle): JSX.Element => (
  <CustomTypography
    variant="h1"
    component="h1"
    children={children}
    sx={sx}
    fontSize={fontSize}
    fontWeight={fontWeight}
    hslLightness={hslLightness}
    hsl={hsl}
  />
);

/**
 * Renders an H2 subheading for content sections.
 *
 * @param {TypographyPropsWithStyle} props
 * @returns {JSX.Element}
 */
export const SubHeading = ({
  children,
  sx,
  fontSize = "1.5rem",
  fontWeight = "bold",
  hslLightness = "dark",
  hsl = defaultHSL,
}: TypographyPropsWithStyle): JSX.Element => (
  <CustomTypography
    variant="h2"
    component="h2"
    children={children}
    sx={sx}
    fontSize={fontSize}
    fontWeight={fontWeight}
    hslLightness={hslLightness}
    hsl={hsl}
  />
);

/**
 * Renders standard paragraph text (body1).
 *
 * @param {TypographyPropsWithStyle} props
 * @returns {JSX.Element}
 */
export const Paragraph = ({
  children,
  sx,
  fontSize = "1rem",
  fontWeight = "medium",
  hslLightness = "dark",
  hsl = defaultHSL,
}: TypographyPropsWithStyle): JSX.Element => (
  <CustomTypography
    variant="body1"
    component="p"
    children={children}
    sx={sx}
    fontSize={fontSize}
    fontWeight={fontWeight}
    hslLightness={hslLightness}
    hsl={hsl}
  />
);

/**
 * Renders smaller supporting text (body2), such as captions.
 *
 * @param {TypographyPropsWithStyle} props
 * @returns {JSX.Element}
 */
export const SmallText = ({
  children,
  sx,
  fontSize = "0.875rem",
  fontWeight = "medium",
  hslLightness = "dark",
  hsl = defaultHSL,
}: TypographyPropsWithStyle): JSX.Element => (
  <CustomTypography
    variant="body2"
    component="span"
    children={children}
    sx={sx}
    fontSize={fontSize}
    fontWeight={fontWeight}
    hslLightness={hslLightness}
    hsl={hsl}
  />
);
